import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface ActivityItem {
  id: string;
  type: "time_entry" | "task_completed" | "comment";
  occurred_at: string;
  title: string;
  detail?: string;
  project_id?: string;
  project_title?: string;
}

/**
 * Henter en blandet aktivitetsstrøm for en bruker siste N dager:
 *  - time_entries (loggede timer)
 *  - budget_line_assignments (fullførte/godkjente oppgaver)
 *  - project_comments (kommentarer skrevet av brukeren)
 *
 * Returnerer items sortert synkende på occurred_at, kuttet til 30.
 */
export function useEmployeeActivity(userId: string | null, days = 14) {
  return useQuery({
    queryKey: ["employee-activity", userId, days],
    enabled: !!userId,
    queryFn: async (): Promise<ActivityItem[]> => {
      if (!userId) return [];
      const sinceDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      const sinceIso = sinceDate.toISOString();
      const sinceDateOnly = sinceIso.slice(0, 10);

      const [timesRes, tasksRes, commentsRes] = await Promise.all([
        supabase
          .from("time_entries")
          .select("id, start_date, duration_seconds, task_description, project_id, projects(name)")
          .eq("user_id", userId)
          .is("deleted_at", null)
          .gte("start_date", sinceDateOnly)
          .order("start_date", { ascending: false })
          .limit(50),
        supabase
          .from("budget_line_assignments")
          .select("id, completed_at, hours, task_status, budget_lines(description, project_id, projects(name))")
          .eq("user_id", userId)
          .in("task_status", ["completed", "approved"])
          .gte("completed_at", sinceIso)
          .order("completed_at", { ascending: false })
          .limit(50),
        supabase
          .from("project_comments")
          .select("id, created_at, body, project_id, projects(name)")
          .eq("author_id", userId)
          .is("deleted_at", null)
          .gte("created_at", sinceIso)
          .order("created_at", { ascending: false })
          .limit(50),
      ]);

      const items: ActivityItem[] = [];

      for (const t of timesRes.data ?? []) {
        const proj = (t as { projects?: { name?: string | null } | null }).projects;
        items.push({
          id: t.id,
          type: "time_entry",
          // start_date er timestamptz i DB; normaliser via Date for å håndtere
          // både date-only og full ISO-string uniformt.
          occurred_at: new Date(t.start_date).toISOString(),
          title: t.task_description ?? "Timer logget",
          detail: `${(((t.duration_seconds ?? 0) as number) / 3600).toFixed(1)}t`,
          project_id: t.project_id ?? undefined,
          project_title: proj?.name ?? undefined,
        });
      }

      for (const a of tasksRes.data ?? []) {
        if (!a.completed_at) continue;
        const bl = (
          a as {
            budget_lines?: {
              description?: string | null;
              project_id?: string | null;
              projects?: { name?: string | null } | null;
            } | null;
          }
        ).budget_lines;
        items.push({
          id: a.id,
          type: "task_completed",
          occurred_at: a.completed_at,
          title: bl?.description ?? "Oppgave fullført",
          detail: `${a.hours ?? 0}t — ${a.task_status === "approved" ? "Godkjent" : "Venter godkjenning"}`,
          project_id: bl?.project_id ?? undefined,
          project_title: bl?.projects?.name ?? undefined,
        });
      }

      for (const c of commentsRes.data ?? []) {
        const proj = (c as { projects?: { name?: string | null } | null }).projects;
        items.push({
          id: c.id,
          type: "comment",
          occurred_at: c.created_at,
          title: "Kommenterte prosjekt",
          detail: (c.body ?? "").slice(0, 100),
          project_id: c.project_id ?? undefined,
          project_title: proj?.name ?? undefined,
        });
      }

      items.sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
      return items.slice(0, 30);
    },
    staleTime: 60_000,
  });
}
